import type { LocalizacaoModel, PedidoLiveModel, PedidoLiveStatus, PedidoProcessadoEvent } from '../../../models/dash/vendas-externas';

export const FEED_MAX_ITEMS = 100;
export const LOCATION_TTL_MS = 15 * 60 * 1000;
export const RECENCY_GREEN_MS = 2 * 60 * 1000;
export const RECENCY_YELLOW_MS = 10 * 60 * 1000;
export const LIVE_POLL_INTERVAL_MS = 30_000;

export type MarkerRecency = 'green' | 'yellow' | 'orange';

const PEDIDO_STATUSES: readonly PedidoLiveStatus[] = ['outbox', 'processado', 'cancelado'];

/**
 * Newest first, unique by id, capped so the live feed stays bounded.
 */
export function prependPedido(existing: readonly PedidoLiveModel[], incoming: PedidoLiveModel, maxItems: number = FEED_MAX_ITEMS): PedidoLiveModel[] {
  const without = existing.filter((pedido: PedidoLiveModel) => pedido.id !== incoming.id);
  return [incoming, ...without].slice(0, maxItems);
}

/**
 * ERP processed the outbox row (D6). Same UUID stays; status/codigo are patched in place.
 */
export function applyPedidoProcessado(existing: readonly PedidoLiveModel[], patch: PedidoProcessadoEvent): PedidoLiveModel[] {
  return existing.map((pedido: PedidoLiveModel) => {
    if (pedido.id !== patch.id) {
      return pedido;
    }
    return {
      ...pedido,
      codigo: patch.codigo === undefined ? pedido.codigo : patch.codigo,
      status: patch.status ?? 'processado',
      total: patch.total === undefined ? pedido.total : patch.total,
    };
  });
}

/**
 * Last GPS ping wins per seller. Drops points older than the Redis TTL (15 min).
 */
export function upsertLocalizacao(
  existing: readonly LocalizacaoModel[],
  incoming: LocalizacaoModel,
  nowMs: number,
  ttlMs: number = LOCATION_TTL_MS
): LocalizacaoModel[] {
  const merged: LocalizacaoModel[] = existing.filter((point: LocalizacaoModel) => point.vendedorId !== incoming.vendedorId);
  merged.push(incoming);
  return pruneLocalizacoes(merged, nowMs, ttlMs);
}

export function pruneLocalizacoes(existing: readonly LocalizacaoModel[], nowMs: number, ttlMs: number = LOCATION_TTL_MS): LocalizacaoModel[] {
  return existing.filter((point: LocalizacaoModel) => nowMs - point.capturedAt < ttlMs);
}

export function markerRecency(capturedAt: number, nowMs: number): MarkerRecency {
  const ageMs: number = nowMs - capturedAt;
  if (ageMs < RECENCY_GREEN_MS) {
    return 'green';
  }
  if (ageMs < RECENCY_YELLOW_MS) {
    return 'yellow';
  }
  return 'orange';
}

export function isPedidoLiveStatus(value: unknown): value is PedidoLiveStatus {
  return typeof value === 'string' && PEDIDO_STATUSES.includes(value as PedidoLiveStatus);
}

export function toPedidoLive(raw: Record<string, unknown>): PedidoLiveModel | undefined {
  if (typeof raw['id'] !== 'string' || raw['id'].length === 0) {
    return undefined;
  }
  const codigoRaw: unknown = raw['codigo'];
  return {
    id: raw['id'],
    codigo: typeof codigoRaw === 'number' ? codigoRaw : null,
    status: isPedidoLiveStatus(raw['status']) ? raw['status'] : 'outbox',
    vendedorCodigo: asNumber(raw['vendedorCodigo']),
    vendedorNome: asString(raw['vendedorNome']),
    clienteNome: asString(raw['clienteNome']),
    total: asNumber(raw['total']),
    createdAt: typeof raw['createdAt'] === 'string' ? raw['createdAt'] : new Date().toISOString(),
  };
}

export function toLocalizacao(raw: Record<string, unknown>, previous?: LocalizacaoModel): LocalizacaoModel | undefined {
  const vendedorId: string = asString(raw['vendedorId']);
  const lat: number = asNumber(raw['lat']);
  const lng: number = asNumber(raw['lng']);
  if (!vendedorId || !Number.isFinite(lat) || !Number.isFinite(lng)) {
    return undefined;
  }
  const capturedAt: number = typeof raw['capturedAt'] === 'number' ? raw['capturedAt'] : Date.now();
  return {
    vendedorId,
    vendedorCodigo: typeof raw['vendedorCodigo'] === 'number' ? raw['vendedorCodigo'] : (previous?.vendedorCodigo ?? 0),
    vendedorNome: asString(raw['vendedorNome']) || previous?.vendedorNome || '',
    lat,
    lng,
    accuracy: typeof raw['accuracy'] === 'number' ? raw['accuracy'] : previous?.accuracy,
    capturedAt,
    receivedAt: typeof raw['receivedAt'] === 'number' ? raw['receivedAt'] : Date.now(),
  };
}

export function toPedidoProcessado(raw: Record<string, unknown>): PedidoProcessadoEvent | undefined {
  if (typeof raw['id'] !== 'string' || raw['id'].length === 0) {
    return undefined;
  }
  return {
    id: raw['id'],
    codigo: typeof raw['codigo'] === 'number' ? raw['codigo'] : undefined,
    status: isPedidoLiveStatus(raw['status']) ? raw['status'] : 'processado',
    total: typeof raw['total'] === 'number' ? raw['total'] : undefined,
    processedAt: typeof raw['processedAt'] === 'string' ? raw['processedAt'] : undefined,
  };
}

function asString(value: unknown): string {
  return typeof value === 'string' ? value : '';
}

function asNumber(value: unknown): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : 0;
}
